import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import {
  FileText,
  RefreshCw,
  Search,
  Filter,
  Building2,
  Calendar,
  DollarSign,
  CheckCircle,
  MoreVertical,
  X,
  Plus,
  Trash2,
  AlertTriangle,
  Clock,
  ChevronDown,
  ChevronRight,
  History,
  Eye,
  Send,
  Download,
  CreditCard,
} from 'lucide-react';
import {
  getInvoices,
  sendInvoice,
  markInvoicePaid,
  downloadInvoicePdf,
  updateInvoiceAdjustments,
  getInvoiceStatistics,
  getInvoiceVersions,
  SubscriptionInvoice,
  InvoiceStatistics,
  formatCurrency,
  parseAmount,
  getStatusColor,
  getStatusLabel,
} from '@/services/billingService';
import { ToastContainer, useToast } from '@/shared/components/ui/Toast';
import { useConfirmDialog } from '@/shared/components/ui/ConfirmDialog';

interface LineItem {
  description: string;
  quantity: number;
  unitPrice: number;
  total: number;
  isReduction?: boolean;
  motif?: string;
}

interface InstitutionSummary {
  tenantId: string;
  tenantName: string;
  planName: string;
  status: 'HEALTHY' | 'OVERDUE' | 'MIXED';
  totalPaid: number;
  totalOutstanding: number;
  invoiceCount: number;
  latestInvoice: SubscriptionInvoice | null;
  invoices: SubscriptionInvoice[];
}

export const InvoicesPage: React.FC = () => {
  const { toasts, removeToast, success, error } = useToast();
  const { confirm, Dialog: ConfirmDialog } = useConfirmDialog();
  const [invoices, setInvoices] = useState<SubscriptionInvoice[]>([]);
  const [statistics, setStatistics] = useState<InvoiceStatistics | null>(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedInvoice, setSelectedInvoice] = useState<SubscriptionInvoice | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showAdjustmentModal, setShowAdjustmentModal] = useState(false);
  const [showVersionHistoryModal, setShowVersionHistoryModal] = useState(false);
  const [versionHistory, setVersionHistory] = useState<SubscriptionInvoice[]>([]);
  const [loadingVersions, setLoadingVersions] = useState(false);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [menuInvoice, setMenuInvoice] = useState<SubscriptionInvoice | null>(null);
  const [adjustments, setAdjustments] = useState<Array<{ description: string; amount: number; motif: string }>>([]);
  const [menuPosition, setMenuPosition] = useState<{ top: number; left: number } | null>(null);
  const [expandedInstitutions, setExpandedInstitutions] = useState<Set<string>>(new Set());
  const [expandedYears, setExpandedYears] = useState<Set<string>>(new Set());
  const menuButtonRefs = useRef<Record<string, HTMLButtonElement | null>>({});

  // Use ref to avoid infinite re-render loop with toast functions
  const toastRef = useRef({ success, error });
  toastRef.current = { success, error };

  const fetchInvoices = useCallback(async () => {
    try {
      setLoading(true);
      const [invoicesData, statsData] = await Promise.all([
        getInvoices({ status: statusFilter || undefined }),
        getInvoiceStatistics(),
      ]);
      setInvoices(invoicesData);
      setStatistics(statsData);
    } catch (err) {
      console.error('Failed to fetch invoices:', err);
      toastRef.current.error('Erreur', 'Impossible de charger les factures');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices]);

  
  // Group invoices by institution
  const getInstitutionSummaries = useCallback((): InstitutionSummary[] => {
    const grouped: Record<string, InstitutionSummary> = {};

    invoices.forEach((inv) => {
      const tenantId = inv.subscription?.tenantId || inv.subscriptionId;
      const tenantName = inv.subscription?.tenant?.name || 'Institution inconnue';
      const planName = inv.subscription?.plan?.name || 'Plan inconnu';

      if (!grouped[tenantId]) {
        grouped[tenantId] = {
          tenantId,
          tenantName,
          planName,
          status: 'HEALTHY',
          totalPaid: 0,
          totalOutstanding: 0,
          invoiceCount: 0,
          latestInvoice: null,
          invoices: [],
        };
      }

      // Only count latest versions for summaries
      if (inv.isLatest !== false) {
        grouped[tenantId].invoiceCount++;
        if (inv.status === 'PAID') {
          grouped[tenantId].totalPaid += parseAmount(inv.total);
        } else if (inv.status !== 'CANCELLED') {
          grouped[tenantId].totalOutstanding += parseAmount(inv.total);
        }
        if (inv.status === 'OVERDUE') {
          grouped[tenantId].status = 'OVERDUE';
        } else if (inv.status !== 'PAID' && grouped[tenantId].status !== 'OVERDUE') {
          grouped[tenantId].status = 'MIXED';
        }
      }

      grouped[tenantId].invoices.push(inv);

      // Track latest invoice
      if (!grouped[tenantId].latestInvoice ||
          new Date(inv.invoiceDate) > new Date(grouped[tenantId].latestInvoice.invoiceDate)) {
        grouped[tenantId].latestInvoice = inv;
      }
    });

    return Object.values(grouped)
      .filter((summary) => {
        if (searchQuery) {
          return summary.tenantName.toLowerCase().includes(searchQuery.toLowerCase());
        }
        return true;
      })
      .sort((a, b) => a.tenantName.localeCompare(b.tenantName));
  }, [invoices, searchQuery]);

  // Group invoices by year and month for an institution
  const getInvoicesByYearMonth = (institutionInvoices: SubscriptionInvoice[]) => {
    const grouped: Record<number, Record<number, SubscriptionInvoice[]>> = {};

    institutionInvoices.forEach((inv) => {
      const year = inv.periodYear || new Date(inv.invoiceDate).getFullYear();
      const month = inv.periodMonth || new Date(inv.invoiceDate).getMonth() + 1;

      if (!grouped[year]) grouped[year] = {};
      if (!grouped[year][month]) grouped[year][month] = [];
      grouped[year][month].push(inv);
    });

    // Sort years descending
    return Object.entries(grouped)
      .sort((a, b) => parseInt(b[0]) - parseInt(a[0]))
      .map(([year, months]) => ({
        year: parseInt(year),
        months: Object.entries(months)
          .sort((a, b) => parseInt(b[0]) - parseInt(a[0]))
          .map(([month, invs]) => ({
            month: parseInt(month),
            invoices: invs.sort((a, b) => (b.version || 1) - (a.version || 1)),
          })),
      }));
  };

  const handleOpenMenu = (id: string, inv: SubscriptionInvoice) => {
    if (openMenuId === id) {
      setOpenMenuId(null);
      setMenuPosition(null);
      setMenuInvoice(null);
      return;
    }

    const button = menuButtonRefs.current[id];
    if (button) {
      const rect = button.getBoundingClientRect();
      const menuHeight = 280;
      const spaceBelow = window.innerHeight - rect.bottom;
      const showAbove = spaceBelow < menuHeight && rect.top > menuHeight;

      setMenuPosition({
        top: showAbove ? rect.top - menuHeight : rect.bottom + 4,
        left: Math.max(8, rect.right - 192),
      });
    }
    setOpenMenuId(id);
    setMenuInvoice(inv);
  };

  const closeMenu = () => {
    setOpenMenuId(null);
    setMenuPosition(null);
    setMenuInvoice(null);
  };

  const handleSend = async (id: string) => {
    setOpenMenuId(null);
    try {
      await sendInvoice(id);
      toastRef.current.success('Facture envoyée', 'La facture a été envoyée avec succès');
      fetchInvoices();
    } catch (err) {
      console.error('Failed to send invoice:', err);
      toastRef.current.error('Erreur', 'Impossible d\'envoyer la facture');
    }
  };

  const handleResend = async (id: string) => {
    setOpenMenuId(null);
    try {
      await sendInvoice(id);
      toastRef.current.success('Facture renvoyée', 'La facture a été renvoyée avec succès');
      fetchInvoices();
    } catch (err) {
      console.error('Failed to resend invoice:', err);
      toastRef.current.error('Erreur', 'Impossible de renvoyer la facture');
    }
  };

  const handleMarkPaid = async (id: string) => {
    setOpenMenuId(null);
    try {
      await markInvoicePaid(id);
      toastRef.current.success('Paiement enregistré', 'La facture a été marquée comme payée');
      fetchInvoices();
      setShowDetailModal(false);
    } catch (err) {
      console.error('Failed to mark invoice as paid:', err);
      toastRef.current.error('Erreur', 'Impossible de marquer la facture comme payée');
    }
  };

  const handleDownload = async (id: string, invoiceNumber: string) => {
    setOpenMenuId(null);
    try {
      await downloadInvoicePdf(id, invoiceNumber);
      toastRef.current.success('Téléchargement', 'La facture PDF a été téléchargée');
    } catch (err) {
      console.error('Failed to download invoice:', err);
      toastRef.current.error('Erreur', 'Impossible de télécharger la facture');
    }
  };

  const handleDelete = async (id: string) => {
    closeMenu();
    const confirmed = await confirm({
      title: 'Supprimer la facture',
      message: 'Êtes-vous sûr de vouloir supprimer cette facture brouillon ? Cette action est irréversible.',
      confirmText: 'Supprimer',
      cancelText: 'Annuler',
      variant: 'danger',
    });

    if (!confirmed) return;

    try {
      const token = localStorage.getItem('nexedu_access_token');
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL || 'http://localhost:4000'}/api/billing/invoices/${id}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        throw new Error('Failed to delete invoice');
      }
      toastRef.current.success('Facture supprimée', 'La facture brouillon a été supprimée');
      fetchInvoices();
    } catch (err) {
      console.error('Failed to delete invoice:', err);
      toastRef.current.error('Erreur', 'Impossible de supprimer la facture');
    }
  };

  const handleViewVersionHistory = async (inv: SubscriptionInvoice) => {
    setOpenMenuId(null);
    setSelectedInvoice(inv);
    setLoadingVersions(true);
    setShowVersionHistoryModal(true);

    try {
      const versions = await getInvoiceVersions(inv.id);
      setVersionHistory(versions);
    } catch (err) {
      console.error('Failed to fetch version history:', err);
      toastRef.current.error('Erreur', 'Impossible de charger l\'historique des versions');
      // Still show modal with just the current invoice
      setVersionHistory([inv]);
    } finally {
      setLoadingVersions(false);
    }
  };

  const handleOpenAdjustments = (inv: SubscriptionInvoice) => {
    setOpenMenuId(null);
    setSelectedInvoice(inv);
    const existingAdjustments = (inv.lineItems as LineItem[])
      .filter(item => item.isReduction || item.motif)
      .map(item => ({
        description: item.description,
        amount: item.total,
        motif: item.motif || '',
      }));
    setAdjustments(existingAdjustments.length > 0 ? existingAdjustments : [{ description: '', amount: 0, motif: '' }]);
    setShowAdjustmentModal(true);
  };

  const handleSaveAdjustments = async () => {
    if (!selectedInvoice) return;

    const validAdjustments = adjustments.filter(adj => adj.description.trim() !== '' && adj.amount !== 0);

    try {
      await updateInvoiceAdjustments(selectedInvoice.id, validAdjustments);
      toastRef.current.success('Ajustements enregistrés', 'Les ajustements ont été appliqués à la facture');
      setShowAdjustmentModal(false);
      fetchInvoices();
    } catch (err) {
      console.error('Failed to save adjustments:', err);
      toastRef.current.error('Erreur', 'Impossible d\'enregistrer les ajustements');
    }
  };

  const addAdjustmentLine = () => {
    setAdjustments([...adjustments, { description: '', amount: 0, motif: '' }]);
  };

  const removeAdjustmentLine = (index: number) => {
    setAdjustments(adjustments.filter((_, i) => i !== index));
  };

  const updateAdjustment = (index: number, field: string, value: string | number) => {
    const updated = [...adjustments];
    updated[index] = { ...updated[index], [field]: value };
    setAdjustments(updated);
  };

  const toggleInstitution = (tenantId: string) => {
    const newExpanded = new Set(expandedInstitutions);
    if (newExpanded.has(tenantId)) {
      newExpanded.delete(tenantId);
    } else {
      newExpanded.add(tenantId);
    }
    setExpandedInstitutions(newExpanded);
  };

  const toggleYear = (key: string) => {
    const newExpanded = new Set(expandedYears);
    if (newExpanded.has(key)) {
      newExpanded.delete(key);
    } else {
      newExpanded.add(key);
    }
    setExpandedYears(newExpanded);
  };

  const getMonthName = (month: number) => {
    return new Date(2026, month - 1, 1).toLocaleDateString('fr-FR', { month: 'long' });
  };

  const getInstitutionStatusBadge = (status: InstitutionSummary['status']) => {
    switch (status) {
      case 'HEALTHY':
        return <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">À jour</span>;
      case 'OVERDUE':
        return <span className="px-2 py-1 text-xs font-medium rounded-full bg-red-100 text-red-800">Impayé</span>;
      case 'MIXED':
        return <span className="px-2 py-1 text-xs font-medium rounded-full bg-yellow-100 text-yellow-800">En cours</span>;
    }
  };

  // Stats
  const stats = {
    total: invoices.filter(i => i.isLatest !== false).length,
    paid: invoices.filter(i => i.status === 'PAID' && i.isLatest !== false).length,
    pending: invoices.filter(i => i.status === 'SENT' && i.isLatest !== false).length,
    overdue: invoices.filter(i => i.status === 'OVERDUE' && i.isLatest !== false).length,
    totalAmount: invoices.filter(i => i.isLatest !== false).reduce((acc, i) => acc + parseAmount(i.total), 0),
    paidAmount: invoices.filter(i => i.status === 'PAID' && i.isLatest !== false).reduce((acc, i) => acc + parseAmount(i.total), 0),
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  const institutionSummaries = getInstitutionSummaries();

  return (
    <>
      <ToastContainer toasts={toasts} onRemove={removeToast} />
      {ConfirmDialog}
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Factures</h1>
            <p className="text-sm text-gray-500 mt-1">
              Gestion des factures par établissement avec historique des versions
            </p>
          </div>
          <button
            onClick={fetchInvoices}
            className="flex items-center gap-2 px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            <RefreshCw size={16} />
            Actualiser
          </button>
        </div>

        {/* Stats Cards - Two rows */}
        {/* Row 1: Status Counts */}
        <div className="grid grid-cols-6 gap-3">
          {/* Draft KPI - Prominent when > 0 */}
          <div className={`rounded-xl p-4 ${(statistics?.byStatus?.DRAFT || 0) > 0 ? 'bg-gradient-to-br from-orange-500 to-amber-500 text-white' : 'bg-white border border-gray-100'}`}>
            <div className="flex items-center justify-between">
              <div>
                <p className={`text-sm ${(statistics?.byStatus?.DRAFT || 0) > 0 ? 'text-orange-100' : 'text-gray-500'}`}>Brouillons</p>
                <p className={`text-2xl font-bold ${(statistics?.byStatus?.DRAFT || 0) > 0 ? 'text-white' : 'text-orange-600'}`}>
                  {statistics?.byStatus?.DRAFT || 0}
                </p>
                {(statistics?.byStatus?.DRAFT || 0) > 0 && (
                  <p className="text-xs text-orange-100 mt-1">À envoyer</p>
                )}
              </div>
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${(statistics?.byStatus?.DRAFT || 0) > 0 ? 'bg-white/20' : 'bg-orange-100'}`}>
                <Clock className={(statistics?.byStatus?.DRAFT || 0) > 0 ? 'text-white' : 'text-orange-600'} size={20} />
              </div>
            </div>
          </div>

          {/* Sent */}
          <div className="bg-white rounded-xl border border-gray-100 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Envoyées</p>
                <p className="text-2xl font-bold text-blue-600">
                  {(statistics?.byStatus?.SENT || 0) + (statistics?.byStatus?.OVERDUE || 0) + (statistics?.byStatus?.PAID || 0)}
                </p>
              </div>
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <Send className="text-blue-600" size={20} />
              </div>
            </div>
          </div>

          {/* Paid */}
          <div className="bg-white rounded-xl border border-gray-100 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Payées</p>
                <p className="text-2xl font-bold text-green-600">{statistics?.byStatus?.PAID || stats.paid}</p>
              </div>
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <CheckCircle className="text-green-600" size={20} />
              </div>
            </div>
          </div>

          {/* En attente (SENT - waiting for payment) */}
          <div className={`rounded-xl p-4 ${(statistics?.byStatus?.SENT || 0) > 0 ? 'bg-gradient-to-br from-amber-400 to-yellow-500 text-white' : 'bg-white border border-gray-100'}`}>
            <div className="flex items-center justify-between">
              <div>
                <p className={`text-sm ${(statistics?.byStatus?.SENT || 0) > 0 ? 'text-amber-100' : 'text-gray-500'}`}>En attente</p>
                <p className={`text-2xl font-bold ${(statistics?.byStatus?.SENT || 0) > 0 ? 'text-white' : 'text-amber-600'}`}>
                  {statistics?.byStatus?.SENT || 0}
                </p>
                {(statistics?.byStatus?.SENT || 0) > 0 && (
                  <p className="text-xs text-amber-100 mt-1">À recouvrer</p>
                )}
              </div>
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${(statistics?.byStatus?.SENT || 0) > 0 ? 'bg-white/20' : 'bg-amber-100'}`}>
                <Clock className={(statistics?.byStatus?.SENT || 0) > 0 ? 'text-white' : 'text-amber-600'} size={20} />
              </div>
            </div>
          </div>

          {/* Overdue - Prominent when > 0 */}
          <div className={`rounded-xl p-4 ${(statistics?.byStatus?.OVERDUE || 0) > 0 ? 'bg-gradient-to-br from-red-500 to-rose-500 text-white' : 'bg-white border border-gray-100'}`}>
            <div className="flex items-center justify-between">
              <div>
                <p className={`text-sm ${(statistics?.byStatus?.OVERDUE || 0) > 0 ? 'text-red-100' : 'text-gray-500'}`}>En retard</p>
                <p className={`text-2xl font-bold ${(statistics?.byStatus?.OVERDUE || 0) > 0 ? 'text-white' : 'text-red-600'}`}>
                  {statistics?.byStatus?.OVERDUE || stats.overdue}
                </p>
                {(statistics?.byStatus?.OVERDUE || 0) > 0 && (
                  <p className="text-xs text-red-100 mt-1">Action requise</p>
                )}
              </div>
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${(statistics?.byStatus?.OVERDUE || 0) > 0 ? 'bg-white/20' : 'bg-red-100'}`}>
                <AlertTriangle className={(statistics?.byStatus?.OVERDUE || 0) > 0 ? 'text-white' : 'text-red-600'} size={20} />
              </div>
            </div>
          </div>

          {/* Total Count */}
          <div className="bg-white rounded-xl border border-gray-100 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total factures</p>
                <p className="text-2xl font-bold text-gray-900">{statistics?.total || stats.total}</p>
              </div>
              <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                <FileText className="text-gray-600" size={20} />
              </div>
            </div>
          </div>
        </div>

        {/* Row 2: Amount Breakdown */}
        <div className="grid grid-cols-3 gap-4">
          <div className="bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl p-4 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-green-100">Montant encaissé</p>
                <p className="text-2xl font-bold">{formatCurrency(statistics?.totals?.paidAmount || stats.paidAmount)}</p>
              </div>
              <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                <CheckCircle className="text-white" size={20} />
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-amber-500 to-orange-500 rounded-xl p-4 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-amber-100">Montant en attente</p>
                <p className="text-2xl font-bold">{formatCurrency(statistics?.totals?.pendingAmount || 0)}</p>
              </div>
              <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                <Clock className="text-white" size={20} />
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl p-4 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-indigo-100">Montant total facturé</p>
                <p className="text-2xl font-bold">{formatCurrency(statistics?.totals?.totalAmount || stats.totalAmount)}</p>
              </div>
              <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                <DollarSign className="text-white" size={20} />
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-4 bg-white p-4 rounded-xl border border-gray-100">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Rechercher par établissement..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          <div className="flex items-center gap-2">
            <Filter size={20} className="text-gray-400" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
            >
              <option value="">Tous les statuts</option>
              <option value="DRAFT">Brouillon</option>
              <option value="SENT">Envoyée</option>
              <option value="PAID">Payée</option>
              <option value="OVERDUE">En retard</option>
              <option value="CANCELLED">Annulée</option>
            </select>
          </div>
        </div>

        {/* Institutions List */}
        <div className="space-y-4">
          {institutionSummaries.map((summary) => {
            const isExpanded = expandedInstitutions.has(summary.tenantId);
            const invoicesByYear = getInvoicesByYearMonth(summary.invoices);

            return (
              <div key={summary.tenantId} className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                {/* Institution Header */}
                <button
                  onClick={() => toggleInstitution(summary.tenantId)}
                  className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center gap-4">
                    {isExpanded ? (
                      <ChevronDown size={20} className="text-gray-400" />
                    ) : (
                      <ChevronRight size={20} className="text-gray-400" />
                    )}
                    <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                      <Building2 className="text-indigo-600" size={20} />
                    </div>
                    <div className="text-left">
                      <h3 className="text-lg font-semibold text-gray-900">
                        {summary.tenantName}
                      </h3>
                      <p className="text-sm text-gray-500">
                        {summary.planName} • {summary.invoiceCount} facture{summary.invoiceCount > 1 ? 's' : ''}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-6">
                    {getInstitutionStatusBadge(summary.status)}
                    <div className="text-right">
                      <p className="text-sm text-gray-500">Total payé</p>
                      <p className="font-semibold text-green-600">{formatCurrency(summary.totalPaid)}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-gray-500">Impayé</p>
                      <p className={`font-semibold ${summary.totalOutstanding > 0 ? 'text-red-600' : 'text-gray-400'}`}>
                        {formatCurrency(summary.totalOutstanding)}
                      </p>
                    </div>
                    {summary.latestInvoice && (
                      <div className="text-right">
                        <p className="text-sm text-gray-500">Dernière facture</p>
                        <p className="font-mono text-sm text-gray-900">{summary.latestInvoice.invoiceNumber}</p>
                      </div>
                    )}
                  </div>
                </button>

                {/* Expanded View - Year/Month Groups */}
                {isExpanded && (
                  <div className="border-t border-gray-100 bg-gray-50 p-4 space-y-3">
                    {/* Summary Cards */}
                    <div className="grid grid-cols-4 gap-3 mb-4">
                      <div className="bg-white rounded-lg p-3 border border-gray-100">
                        <p className="text-xs text-gray-500">Total facturé</p>
                        <p className="text-lg font-bold text-gray-900">
                          {formatCurrency(summary.totalPaid + summary.totalOutstanding)}
                        </p>
                      </div>
                      <div className="bg-white rounded-lg p-3 border border-gray-100">
                        <p className="text-xs text-gray-500">Payé</p>
                        <p className="text-lg font-bold text-green-600">{formatCurrency(summary.totalPaid)}</p>
                      </div>
                      <div className="bg-white rounded-lg p-3 border border-gray-100">
                        <p className="text-xs text-gray-500">Impayé</p>
                        <p className="text-lg font-bold text-red-600">{formatCurrency(summary.totalOutstanding)}</p>
                      </div>
                      <div className="bg-white rounded-lg p-3 border border-gray-100">
                        <p className="text-xs text-gray-500">Taux recouvrement</p>
                        <p className="text-lg font-bold text-indigo-600">
                          {summary.totalPaid + summary.totalOutstanding > 0
                            ? Math.round((summary.totalPaid / (summary.totalPaid + summary.totalOutstanding)) * 100)
                            : 0}%
                        </p>
                      </div>
                    </div>

                    {/* Years Accordion */}
                    {invoicesByYear.map(({ year, months }) => {
                      const yearKey = `${summary.tenantId}-${year}`;
                      const isYearExpanded = expandedYears.has(yearKey);
                      const yearTotal = months.flatMap(m => m.invoices)
                        .filter(inv => inv.isLatest !== false)
                        .reduce((acc, inv) => acc + parseAmount(inv.total), 0);

                      return (
                        <div key={yearKey} className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                          {/* Year Header */}
                          <button
                            onClick={() => toggleYear(yearKey)}
                            className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50"
                          >
                            <div className="flex items-center gap-3">
                              {isYearExpanded ? (
                                <ChevronDown size={16} className="text-gray-400" />
                              ) : (
                                <ChevronRight size={16} className="text-gray-400" />
                              )}
                              <Calendar size={16} className="text-indigo-500" />
                              <span className="font-semibold text-gray-900">{year}</span>
                              <span className="text-sm text-gray-500">
                                ({months.reduce((acc, m) => acc + m.invoices.filter(i => i.isLatest !== false).length, 0)} factures)
                              </span>
                            </div>
                            <span className="font-semibold text-gray-900">{formatCurrency(yearTotal)}</span>
                          </button>

                          {/* Months */}
                          {isYearExpanded && (
                            <div className="border-t border-gray-100">
                              {months.map(({ month, invoices: monthInvoices }) => {
                                const latestInvoices = monthInvoices.filter(inv => inv.isLatest !== false);
                                const hasVersions = monthInvoices.some(inv => (inv.version || 1) > 1);

                                return (
                                  <div key={`${yearKey}-${month}`} className="border-b border-gray-100 last:border-b-0">
                                    <div className="px-4 py-2 bg-gray-50 flex items-center justify-between">
                                      <span className="text-sm font-medium text-gray-700 capitalize">
                                        {getMonthName(month)}
                                      </span>
                                      {hasVersions && (
                                        <span className="text-xs text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded-full">
                                          Versions disponibles
                                        </span>
                                      )}
                                    </div>
                                    <table className="w-full">
                                      <thead>
                                        <tr className="text-xs text-gray-500 uppercase">
                                          <th className="px-4 py-2 text-left">N° Facture</th>
                                          <th className="px-4 py-2 text-left">Date</th>
                                          <th className="px-4 py-2 text-right">HT</th>
                                          <th className="px-4 py-2 text-right">TTC</th>
                                          <th className="px-4 py-2 text-left">Statut</th>
                                          <th className="px-4 py-2 text-center">Version</th>
                                          <th className="px-4 py-2 text-center">Actions</th>
                                        </tr>
                                      </thead>
                                      <tbody className="divide-y divide-gray-50">
                                        {latestInvoices.map((inv) => (
                                          <tr key={inv.id} className="hover:bg-gray-50">
                                            <td className="px-4 py-2">
                                              <span className="font-mono text-sm text-gray-900">{inv.invoiceNumber}</span>
                                            </td>
                                            <td className="px-4 py-2 text-sm text-gray-500">
                                              {new Date(inv.invoiceDate).toLocaleDateString('fr-FR')}
                                            </td>
                                            <td className="px-4 py-2 text-sm text-right text-gray-900">
                                              {formatCurrency(inv.subtotal)}
                                            </td>
                                            <td className="px-4 py-2 text-sm text-right font-semibold text-gray-900">
                                              {formatCurrency(inv.total)}
                                            </td>
                                            <td className="px-4 py-2">
                                              <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${getStatusColor(inv.status)}`}>
                                                {getStatusLabel(inv.status)}
                                              </span>
                                            </td>
                                            <td className="px-4 py-2 text-center">
                                              {(inv.version || 1) > 1 ? (
                                                <button
                                                  onClick={() => handleViewVersionHistory(inv)}
                                                  className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-indigo-100 text-indigo-700 hover:bg-indigo-200"
                                                >
                                                  <History size={12} />
                                                  V{inv.version}
                                                </button>
                                              ) : (
                                                <span className="text-xs text-gray-400">V1</span>
                                              )}
                                            </td>
                                            <td className="px-4 py-2">
                                              <div className="flex justify-center">
                                                <button
                                                  ref={(el) => { menuButtonRefs.current[inv.id] = el; }}
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleOpenMenu(inv.id, inv);
                                                  }}
                                                  className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded"
                                                >
                                                  <MoreVertical size={16} />
                                                </button>
                                              </div>
                                            </td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}

          {institutionSummaries.length === 0 && (
            <div className="bg-white rounded-xl border border-gray-100 p-12 text-center text-gray-500">
              <FileText size={48} className="mx-auto text-gray-300 mb-4" />
              <p className="text-lg font-medium text-gray-900">Aucune facture trouvée</p>
              <p className="text-sm text-gray-500 mt-1">
                Les factures apparaîtront ici une fois générées
              </p>
            </div>
          )}
        </div>

        {/* Version History Modal */}
        {showVersionHistoryModal && selectedInvoice && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowVersionHistoryModal(false)}>
            <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[80vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
              <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-indigo-600 to-blue-600 text-white">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-xl font-semibold flex items-center gap-2">
                      <History size={20} />
                      Historique des versions
                    </h2>
                    <p className="text-indigo-100 text-sm mt-1">
                      Période: {getMonthName(selectedInvoice.periodMonth || 1)} {selectedInvoice.periodYear}
                    </p>
                  </div>
                  <button onClick={() => setShowVersionHistoryModal(false)} className="text-white/80 hover:text-white">
                    <X size={20} />
                  </button>
                </div>
              </div>

              <div className="p-6 overflow-y-auto max-h-[60vh]">
                {loadingVersions ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {versionHistory.map((version, idx) => (
                      <div
                        key={version.id}
                        className={`border rounded-lg p-4 ${version.isLatest ? 'border-indigo-300 bg-indigo-50' : 'border-gray-200'}`}
                      >
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <span className={`px-2 py-1 text-xs font-bold rounded ${
                              version.isLatest
                                ? 'bg-indigo-600 text-white'
                                : 'bg-gray-200 text-gray-600'
                            }`}>
                              V{version.version || 1}
                            </span>
                            <span className="font-mono text-sm text-gray-900">{version.invoiceNumber}</span>
                            {version.isLatest && (
                              <span className="text-xs text-indigo-600 bg-indigo-100 px-2 py-0.5 rounded-full">
                                Version actuelle
                              </span>
                            )}
                          </div>
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(version.status)}`}>
                            {getStatusLabel(version.status)}
                          </span>
                        </div>

                        <div className="grid grid-cols-3 gap-4 text-sm">
                          <div>
                            <p className="text-gray-500">Date création</p>
                            <p className="font-medium">{new Date(version.createdAt).toLocaleDateString('fr-FR')}</p>
                          </div>
                          <div>
                            <p className="text-gray-500">Montant HT</p>
                            <p className="font-medium">{formatCurrency(version.subtotal)}</p>
                          </div>
                          <div>
                            <p className="text-gray-500">Total TTC</p>
                            <p className="font-bold text-indigo-600">{formatCurrency(version.total)}</p>
                          </div>
                        </div>

                        {/* Show line items for this version */}
                        {(version.lineItems as LineItem[]).length > 0 && (
                          <div className="mt-3 pt-3 border-t border-gray-100">
                            <p className="text-xs text-gray-500 mb-2">Lignes de facturation:</p>
                            <div className="space-y-1">
                              {(version.lineItems as LineItem[]).map((item, itemIdx) => (
                                <div key={itemIdx} className={`flex justify-between text-xs ${item.isReduction ? 'text-red-600' : 'text-gray-600'}`}>
                                  <span>{item.description}</span>
                                  <span>{formatCurrency(item.total)}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        <div className="mt-3 flex justify-end">
                          <button
                            onClick={() => handleDownload(version.id, version.invoiceNumber)}
                            className="flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-800"
                          >
                            <Download size={12} />
                            Télécharger PDF
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Detail Modal */}
        {showDetailModal && selectedInvoice && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowDetailModal(false)}>
            <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
              <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-indigo-600 to-blue-600 text-white rounded-t-xl">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-xl font-semibold">Facture {selectedInvoice.invoiceNumber}</h2>
                    <p className="text-indigo-100 text-sm mt-1">
                      {new Date(selectedInvoice.invoiceDate).toLocaleDateString('fr-FR')}
                      {(selectedInvoice.version || 1) > 1 && ` • Version ${selectedInvoice.version}`}
                    </p>
                  </div>
                  <span className={`px-3 py-1 text-sm font-medium rounded-full bg-white/20`}>
                    {getStatusLabel(selectedInvoice.status)}
                  </span>
                </div>
              </div>

              <div className="p-6 space-y-6">
                {/* Client Info */}
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <h3 className="text-sm font-semibold text-gray-500 uppercase mb-2">Client</h3>
                    <p className="font-medium text-gray-900">{selectedInvoice.subscription?.tenant?.name}</p>
                    {selectedInvoice.subscription?.tenant?.ice && (
                      <p className="text-sm text-gray-500">ICE: {selectedInvoice.subscription.tenant.ice}</p>
                    )}
                    {selectedInvoice.subscription?.tenant?.ifiscal && (
                      <p className="text-sm text-gray-500">IF: {selectedInvoice.subscription.tenant.ifiscal}</p>
                    )}
                  </div>
                  <div className="text-right">
                    <h3 className="text-sm font-semibold text-gray-500 uppercase mb-2">Dates</h3>
                    <p className="text-sm text-gray-600">
                      <span className="text-gray-500">Date:</span> {new Date(selectedInvoice.invoiceDate).toLocaleDateString('fr-FR')}
                    </p>
                    <p className="text-sm text-gray-600">
                      <span className="text-gray-500">Échéance:</span> {new Date(selectedInvoice.dueDate).toLocaleDateString('fr-FR')}
                    </p>
                    {selectedInvoice.paidAt && (
                      <p className="text-sm text-green-600">
                        <span className="text-gray-500">Payée le:</span> {new Date(selectedInvoice.paidAt).toLocaleDateString('fr-FR')}
                      </p>
                    )}
                  </div>
                </div>

                {/* Line Items */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-500 uppercase mb-2">Lignes</h3>
                  <div className="border border-gray-200 rounded-lg overflow-hidden">
                    <table className="w-full">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Description</th>
                          <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">Qté</th>
                          <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">PU HT</th>
                          <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">Total HT</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {(selectedInvoice.lineItems as LineItem[]).map((item, idx) => (
                          <tr key={idx} className={item.isReduction ? 'text-red-600' : ''}>
                            <td className="px-4 py-2 text-sm">
                              {item.description}
                              {item.motif && (
                                <span className="block text-xs text-gray-500">{item.motif}</span>
                              )}
                            </td>
                            <td className="px-4 py-2 text-sm text-right">{item.quantity}</td>
                            <td className="px-4 py-2 text-sm text-right">{formatCurrency(item.unitPrice)}</td>
                            <td className="px-4 py-2 text-sm text-right">{formatCurrency(item.total)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Totals */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex justify-between py-1">
                    <span className="text-gray-500">Sous-total HT</span>
                    <span className="font-medium">{formatCurrency(selectedInvoice.subtotal)}</span>
                  </div>
                  <div className="flex justify-between py-1">
                    <span className="text-gray-500">TVA ({selectedInvoice.tvaRate}%)</span>
                    <span className="font-medium">{formatCurrency(selectedInvoice.tvaAmount)}</span>
                  </div>
                  <div className="flex justify-between py-2 border-t border-gray-200 mt-2">
                    <span className="font-semibold text-gray-900">Total TTC</span>
                    <span className="text-xl font-bold text-indigo-600">{formatCurrency(selectedInvoice.total)}</span>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                  {(selectedInvoice.version || 1) > 1 && (
                    <button
                      onClick={() => {
                        setShowDetailModal(false);
                        handleViewVersionHistory(selectedInvoice);
                      }}
                      className="flex items-center gap-2 px-4 py-2 text-indigo-600 bg-indigo-50 rounded-lg hover:bg-indigo-100"
                    >
                      <History size={16} />
                      Historique versions
                    </button>
                  )}
                  {(selectedInvoice.status === 'SENT' || selectedInvoice.status === 'OVERDUE') && (
                    <button
                      onClick={() => handleMarkPaid(selectedInvoice.id)}
                      className="flex items-center gap-2 px-4 py-2 text-white bg-green-600 rounded-lg hover:bg-green-700"
                    >
                      Marquer payée
                    </button>
                  )}
                  <button
                    onClick={() => setShowDetailModal(false)}
                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                  >
                    Fermer
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Adjustment Modal */}
        {showAdjustmentModal && selectedInvoice && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowAdjustmentModal(false)}>
            <div className="bg-white rounded-xl shadow-xl max-w-xl w-full" onClick={(e) => e.stopPropagation()}>
              <div className="p-6 border-b border-gray-200 flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-900">Ajustements - {selectedInvoice.invoiceNumber}</h2>
                <button onClick={() => setShowAdjustmentModal(false)} className="text-gray-400 hover:text-gray-600">
                  <X size={20} />
                </button>
              </div>

              <div className="p-6 space-y-4">
                <p className="text-sm text-gray-500">
                  Ajoutez des réductions ou des frais supplémentaires à cette facture.
                  Les montants négatifs sont des réductions.
                </p>

                {adjustments.map((adj, idx) => (
                  <div key={idx} className="border border-gray-200 rounded-lg p-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-700">Ligne {idx + 1}</span>
                      {adjustments.length > 1 && (
                        <button
                          onClick={() => removeAdjustmentLine(idx)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <Trash2 size={16} />
                        </button>
                      )}
                    </div>
                    <input
                      type="text"
                      placeholder="Description (ex: Réduction commerciale)"
                      value={adj.description}
                      onChange={(e) => updateAdjustment(idx, 'description', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                    />
                    <div className="flex gap-3">
                      <input
                        type="number"
                        placeholder="Montant (négatif = réduction)"
                        value={adj.amount || ''}
                        onChange={(e) => updateAdjustment(idx, 'amount', parseFloat(e.target.value) || 0)}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                      />
                    </div>
                    <input
                      type="text"
                      placeholder="Motif (optionnel)"
                      value={adj.motif}
                      onChange={(e) => updateAdjustment(idx, 'motif', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                ))}

                <button
                  onClick={addAdjustmentLine}
                  className="flex items-center gap-2 text-sm text-indigo-600 hover:text-indigo-800"
                >
                  <Plus size={16} />
                  Ajouter une ligne
                </button>
              </div>

              <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
                <button
                  onClick={() => setShowAdjustmentModal(false)}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                >
                  Annuler
                </button>
                <button
                  onClick={handleSaveAdjustments}
                  className="px-4 py-2 text-white bg-indigo-600 rounded-lg hover:bg-indigo-700"
                >
                  Enregistrer
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Portal-based Dropdown Menu */}
      {openMenuId && menuInvoice && menuPosition && createPortal(
        <>
          {/* Backdrop to close menu */}
          <div
            className="fixed inset-0 z-[9998]"
            onClick={closeMenu}
          />
          {/* Menu */}
          <div
            className="fixed w-48 bg-white rounded-lg shadow-xl border border-gray-200 py-1 z-[9999]"
            style={{ top: menuPosition.top, left: menuPosition.left }}
          >
            <button
              onClick={() => {
                closeMenu();
                setSelectedInvoice(menuInvoice);
                setShowDetailModal(true);
              }}
              className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
            >
              <Eye size={14} className="text-gray-400" />
              Voir détails
            </button>
            <button
              onClick={() => handleDownload(menuInvoice.id, menuInvoice.invoiceNumber)}
              className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
            >
              <Download size={14} className="text-gray-400" />
              Télécharger PDF
            </button>
            {(menuInvoice.version || 1) > 1 && (
              <button
                onClick={() => handleViewVersionHistory(menuInvoice)}
                className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
              >
                <History size={14} className="text-gray-400" />
                Historique versions
              </button>
            )}
            {menuInvoice.status === 'DRAFT' && (
              <>
                <div className="border-t border-gray-100 my-1" />
                <button
                  onClick={() => handleSend(menuInvoice.id)}
                  className="w-full px-4 py-2 text-left text-sm text-blue-600 hover:bg-blue-50 flex items-center gap-2"
                >
                  <Send size={14} />
                  Envoyer la facture
                </button>
                <button
                  onClick={() => {
                    const inv = menuInvoice;
                    closeMenu();
                    handleOpenAdjustments(inv);
                  }}
                  className="w-full px-4 py-2 text-left text-sm text-orange-600 hover:bg-orange-50 flex items-center gap-2"
                >
                  <Plus size={14} />
                  Ajouter ajustement
                </button>
                <div className="border-t border-gray-100 my-1" />
                <button
                  onClick={() => handleDelete(menuInvoice.id)}
                  className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                >
                  <Trash2 size={14} />
                  Supprimer
                </button>
              </>
            )}
            {(menuInvoice.status === 'SENT' || menuInvoice.status === 'OVERDUE') && (
              <>
                <div className="border-t border-gray-100 my-1" />
                <button
                  onClick={() => handleResend(menuInvoice.id)}
                  className="w-full px-4 py-2 text-left text-sm text-blue-600 hover:bg-blue-50 flex items-center gap-2"
                >
                  <Send size={14} />
                  Renvoyer la facture
                </button>
                <button
                  onClick={() => handleMarkPaid(menuInvoice.id)}
                  className="w-full px-4 py-2 text-left text-sm text-green-600 hover:bg-green-50 flex items-center gap-2"
                >
                  <CreditCard size={14} />
                  Marquer comme payée
                </button>
              </>
            )}
          </div>
        </>,
        document.body
      )}
    </>
  );
};

export default InvoicesPage;
